import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, User, Phone, Mail, Briefcase } from 'lucide-react';
import '../styles/MasterForms.css';
import apiClient from '../services/api';

const Employees = () => {
  // ✅ Get session data from localStorage
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  // ✅ ALL HOOKS FIRST (before any conditional returns)
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [employeeTypes, setEmployeeTypes] = useState([]);
const [employmentStatuses, setEmploymentStatuses] = useState([]);

  // Dropdown options
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employeesList, setEmployeesList] = useState([]); // For reporting to dropdown
  
 const [formData, setFormData] = useState({
  employeeid: null,
  companyid: companyId || null,
  employeecode: '',
  employeename: '',
  dateofbirth: '',
  gender: '',
  maritalstatus: '',
  bloodgroup: '',
  nationalid: '',
  passportnumber: '',
  passportexpiry: '',
  email: '',
  phone: '',
  mobile: '',
  addressline1: '',
  addressline2: '',
  cityid: '',
  stateid: '',
  postalcode: '',
  countryid: '',
  nationalityid: '',
  departmentid: '',
  designationid: '',
  employeetypeid: '',        // ✅ CHANGED: Empty string (not 1)
  joiningdate: '',
  leavingdate: '',
  employmentstatusid: '',    // ✅ CHANGED: Empty string (not 1)
  reportingto: '',
  basicsalary: '',
  accountnumber: '',
  iban: '',
  isactive: true
});
  
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !yearId) {
      setLoading(false);
      return;
    }
    fetchDropdownData();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, yearId]);

  useEffect(() => {
    filterEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, employees]);

 const fetchDropdownData = async () => {
  try {
    // ✅ FIXED: Added empTypesRes and empStatusRes to destructuring
    const [citiesRes, statesRes, countriesRes, deptsRes, designsRes, empTypesRes, empStatusRes] = await Promise.all([
      apiClient.get('/Cities.php', { params: { isactive: 1 } }),
      apiClient.get('/States.php', { params: { isactive: 1 } }),
      apiClient.get('/Countries.php', { params: { isactive: 1 } }),
      apiClient.get('/Departments.php'),
      apiClient.get('/Designations.php'),
      apiClient.get('/EmployeeTypes.php'),
      apiClient.get('/EmploymentStatuses.php')
    ]);

    if (citiesRes.data.success) setCities(citiesRes.data.data || []);
    if (statesRes.data.success) setStates(statesRes.data.data || []);
    if (countriesRes.data.success) setCountries(countriesRes.data.data || []);
    if (deptsRes.data.success) setDepartments(deptsRes.data.data || []);
    if (designsRes.data.success) setDesignations(designsRes.data.data || []);
    if (empTypesRes.data.success) setEmployeeTypes(empTypesRes.data.data || []);
    if (empStatusRes.data.success) setEmploymentStatuses(empStatusRes.data.data || []);
  } catch (error) {
    console.error('Error fetching dropdown data:', error);
    if (error.response?.status === 401) {
      alert('Session expired. Please login again.');
    }
  }
};


  const fetchEmployees = async () => {
    setLoading(true);
    try {
      // ✅ Use apiClient with session companyId
      const response = await apiClient.get('/Employees.php', {
        params: { companyid: companyId }
      });
      
      if (response.data.success) {
        setEmployees(response.data.data || []);
        setEmployeesList(response.data.data || []); // For reporting to dropdown
      } else {
        alert('Failed to fetch employees: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error fetching employees. Please check if backend is running.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
    } else {
      const filtered = employees.filter(emp =>
        emp.employeename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employeecode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmployees(filtered);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.employeename.trim()) {
      newErrors.employeename = 'Employee name is required';
    }
     if (!formData.employeetypeid) {
    newErrors.employeetypeid = 'Employee type is required';
  }
  if (!formData.employmentstatusid) {
    newErrors.employmentstatusid = 'Employment status is required';
  }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    if (formData.mobile && formData.mobile.length < 7) {
      newErrors.mobile = 'Mobile number must be at least 7 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    return;
  }
  
  setSubmitLoading(true);
  
  try {
    // ✅ Prepare data with proper null handling
    const dataToSend = {
      ...formData,
      companyid: companyId,
      isactive: formData.isactive ? 1 : 0,
      
      // Convert empty strings to null for integer fields
      cityid: formData.cityid || null,
      stateid: formData.stateid || null,
      countryid: formData.countryid || null,
      nationalityid: formData.nationalityid || null,
      departmentid: formData.departmentid || null,
      designationid: formData.designationid || null,
      employeetypeid: formData.employeetypeid || null,
      employmentstatusid: formData.employmentstatusid || null,
      reportingto: formData.reportingto || null,
      
      // Convert empty strings to null for optional fields
      dateofbirth: formData.dateofbirth || null,
      gender: formData.gender || null,
      maritalstatus: formData.maritalstatus || null,
      bloodgroup: formData.bloodgroup || null,
      nationalid: formData.nationalid || null,
      passportnumber: formData.passportnumber || null,
      passportexpiry: formData.passportexpiry || null,
      email: formData.email || null,
      phone: formData.phone || null,
      mobile: formData.mobile || null,
      addressline1: formData.addressline1 || null,
      addressline2: formData.addressline2 || null,
      postalcode: formData.postalcode || null,
      joiningdate: formData.joiningdate || null,
      leavingdate: formData.leavingdate || null,
      basicsalary: formData.basicsalary || null,
      accountnumber: formData.accountnumber || null,
      iban: formData.iban || null
    };
    
    const method = editMode ? 'put' : 'post';
    const response = await apiClient[method]('/Employees.php', dataToSend);
    
    const data = response.data;
    
    if (data.success) {
      if (!editMode && data.data && data.data.employeecode) {
        alert(`Employee created successfully!\nEmployee Code: ${data.data.employeecode}`);
      } else {
        alert(editMode ? 'Employee updated successfully!' : 'Employee created successfully!');
      }
      setShowModal(false);
      resetForm();
      fetchEmployees();
    } else {
      alert('Error: ' + data.message);
    }
  } catch (error) {
    console.error('Error saving employee:', error);
    if (error.response?.status === 401) {
      alert('Session expired. Please login again.');
    } else if (error.response?.data?.message) {
      alert('Error: ' + error.response.data.message);
    } else {
      alert('Error saving employee. Please try again.');
    }
  } finally {
    setSubmitLoading(false);
  }
};

  const handleEdit = (employee) => {
    setFormData({
      employeeid: employee.employeeid,
      companyid: employee.companyid,
      employeecode: employee.employeecode,
      employeename: employee.employeename,
      dateofbirth: employee.dateofbirth || '',
      gender: employee.gender || '',
      maritalstatus: employee.maritalstatus || '',
      bloodgroup: employee.bloodgroup || '',
      nationalid: employee.nationalid || '',
      passportnumber: employee.passportnumber || '',
      passportexpiry: employee.passportexpiry || '',
      email: employee.email || '',
      phone: employee.phone || '',
      mobile: employee.mobile || '',
      addressline1: employee.addressline1 || '',
      addressline2: employee.addressline2 || '',
      cityid: employee.cityid || '',
      stateid: employee.stateid || '',
      postalcode: employee.postalcode || '',
      countryid: employee.countryid || '',
      nationalityid: employee.nationalityid || '',
      departmentid: employee.departmentid || '',
      designationid: employee.designationid || '',
      employeetypeid: employee.employeetypeid || '',
      joiningdate: employee.joiningdate || '',
      leavingdate: employee.leavingdate || '',
      employmentstatusid: employee.employmentstatusid || '',
      reportingto: employee.reportingto || '',
      basicsalary: employee.basicsalary || '',
      accountnumber: employee.accountnumber || '',
      iban: employee.iban || '',
      isactive: employee.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) {
      return;
    }
    
    try {
      // ✅ Use apiClient
      const response = await apiClient.delete('/Employees.php', {
        data: { employeeid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Employee deleted successfully!');
        fetchEmployees();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting employee. Please try again.');
      }
    }
  };

  const resetForm = () => {
  setFormData({
    employeeid: null,
    companyid: companyId || null,
    employeecode: '',
    employeename: '',
    dateofbirth: '',
    gender: '',
    maritalstatus: '',
    bloodgroup: '',
    nationalid: '',
    passportnumber: '',
    passportexpiry: '',
    email: '',
    phone: '',
    mobile: '',
    addressline1: '',
    addressline2: '',
    cityid: '',
    stateid: '',
    postalcode: '',
    countryid: '',
    nationalityid: '',
    departmentid: '',
    designationid: '',
    employeetypeid: '',        // ✅ CHANGED: Empty string (not 1)
    joiningdate: '',
    leavingdate: '',
    employmentstatusid: '',    // ✅ CHANGED: Empty string (not 1)
    reportingto: '',
    basicsalary: '',
    accountnumber: '',
    iban: '',
    isactive: true
  });
  setEditMode(false);
  setErrors({});
};

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ✅ Check session context - AFTER all hooks
  if (!companyId || !yearId) {
    return (
      <div className="master-container">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Employees Master</h2>
          <p className="subtitle">Manage employee information</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Employee
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by name, code, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          Total: {filteredEmployees.length} {filteredEmployees.length === 1 ? 'employee' : 'employees'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading employees...</div>
      ) : filteredEmployees.length === 0 ? (
        <div className="empty-state">
          <p>No employees found</p>
          {searchTerm && <button onClick={() => setSearchTerm('')}>Clear search</button>}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Employee Name</th>
                <th>Department</th>
                <th>Designation</th>
                <th>Contact</th>
                <th>Joining Date</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((employee) => (
                <tr key={employee.employeeid}>
                  <td className="font-mono">{employee.employeecode}</td>
                  <td className="font-semibold">{employee.employeename}</td>
                  <td>{employee.departmentname || '-'}</td>
                  <td>{employee.designationname || '-'}</td>
                  <td className="contact-cell">
                    {employee.mobile && (
                      <div className="contact-item">
                        <Phone size={14} />
                        <span>{employee.mobile}</span>
                      </div>
                    )}
                    {employee.email && (
                      <div className="contact-item">
                        <Mail size={14} />
                        <span>{employee.email}</span>
                      </div>
                    )}
                  </td>
                  <td>{employee.joiningdate ? new Date(employee.joiningdate).toLocaleDateString() : '-'}</td>
                  <td>
                    <span className={`status-badge ${employee.isactive ? 'active' : 'inactive'}`}>
                      {employee.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(employee)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(employee.employeeid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Employee' : 'Add New Employee'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              {/* Basic Information */}
              <div className="form-section">
                <h4>Basic Information</h4>
                {editMode && formData.employeecode && (
                  <div className="form-group">
                    <label>Employee Code (Auto-generated)</label>
                    <input
                      type="text"
                      value={formData.employeecode}
                      disabled
                      style={{ background: '#f3f4f6', cursor: 'not-allowed' }}
                    />
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="employeename">
                      Employee Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="employeename"
                      value={formData.employeename}
                      onChange={(e) => setFormData({ ...formData, employeename: e.target.value })}
                      maxLength="100"
                      placeholder="Full Name"
                      className={errors.employeename ? 'error' : ''}
                    />
                    {errors.employeename && (
                      <span className="error-message">{errors.employeename}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="dateofbirth">Date of Birth</label>
                    <input
                      type="date"
                      id="dateofbirth"
                      value={formData.dateofbirth}
                      onChange={(e) => setFormData({ ...formData, dateofbirth: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="gender">Gender</label>
                    <select
                      id="gender"
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="maritalstatus">Marital Status</label>
                    <select
                      id="maritalstatus"
                      value={formData.maritalstatus}
                      onChange={(e) => setFormData({ ...formData, maritalstatus: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="bloodgroup">Blood Group</label>
                    <select
                      id="bloodgroup"
                      value={formData.bloodgroup}
                      onChange={(e) => setFormData({ ...formData, bloodgroup: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Identity Documents */}
              <div className="form-section">
                <h4>Identity Documents</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="nationalid">National ID</label>
                    <input
                      type="text"
                      id="nationalid"
                      value={formData.nationalid}
                      onChange={(e) => setFormData({ ...formData, nationalid: e.target.value })}
                      maxLength="50"
                      placeholder="National ID Number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="passportnumber">Passport Number</label>
                    <input
                      type="text"
                      id="passportnumber"
                      value={formData.passportnumber}
                      onChange={(e) => setFormData({ ...formData, passportnumber: e.target.value })}
                      maxLength="50"
                      placeholder="Passport Number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="passportexpiry">Passport Expiry</label>
                    <input
                      type="date"
                      id="passportexpiry"
                      value={formData.passportexpiry}
                      onChange={(e) => setFormData({ ...formData, passportexpiry: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="form-section">
                <h4>Contact Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      maxLength="100"
                      placeholder="email@example.com"
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && (
                      <span className="error-message">{errors.email}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      maxLength="20"
                      placeholder="Phone Number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="mobile">Mobile</label>
                    <input
                      type="tel"
                      id="mobile"
                      value={formData.mobile}
                      onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      maxLength="20"
                      placeholder="Mobile Number"
                      className={errors.mobile ? 'error' : ''}
                    />
                    {errors.mobile && (
                      <span className="error-message">{errors.mobile}</span>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="addressline1">Address Line 1</label>
                  <input
                    type="text"
                    id="addressline1"
                    value={formData.addressline1}
                    onChange={(e) => setFormData({ ...formData, addressline1: e.target.value })}
                    maxLength="255"
                    placeholder="Street, Building"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="addressline2">Address Line 2</label>
                  <input
                    type="text"
                    id="addressline2"
                    value={formData.addressline2}
                    onChange={(e) => setFormData({ ...formData, addressline2: e.target.value })}
                    maxLength="255"
                    placeholder="Area, Landmark"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cityid">City</label>
                    <select
                      id="cityid"
                      value={formData.cityid}
                      onChange={(e) => setFormData({ ...formData, cityid: e.target.value })}
                    >
                      <option value="">-- Select City --</option>
                      {cities.map((city) => (
                        <option key={city.cityid} value={city.cityid}>
                          {city.cityname}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="postalcode">Postal Code</label>
                    <input
                      type="text"
                      id="postalcode"
                      value={formData.postalcode}
                      onChange={(e) => setFormData({ ...formData, postalcode: e.target.value })}
                      maxLength="20"
                      placeholder="Postal/ZIP Code"
                    />
                  </div>
                </div>
              </div>

              {/* Employment Details */}
              <div className="form-section">
                <h4>Employment Details</h4>
                 <div className="form-row">
                    {/* ✅ NEW: Employee Type Dropdown */}
                    <div className="form-group">
                      <label htmlFor="employeetypeid">
                        Employee Type <span className="required">*</span>
                      </label>
                      <select
                        id="employeetypeid"
                        value={formData.employeetypeid}
                        onChange={(e) => setFormData({ ...formData, employeetypeid: e.target.value })}
                        className={errors.employeetypeid ? 'error' : ''}
                        required
                      >
                        <option value="">-- Select Employee Type --</option>
                        {employeeTypes.map((type) => (
                          <option key={type.employeetypeid} value={type.employeetypeid}>
                            {type.typename}
                          </option>
                        ))}
                      </select>
                      {errors.employeetypeid && (
                        <span className="error-message">{errors.employeetypeid}</span>
                      )}
                    </div>

                    {/* ✅ NEW: Employment Status Dropdown */}
                    <div className="form-group">
                      <label htmlFor="employmentstatusid">
                        Employment Status <span className="required">*</span>
                      </label>
                      <select
                        id="employmentstatusid"
                        value={formData.employmentstatusid}
                        onChange={(e) => setFormData({ ...formData, employmentstatusid: e.target.value })}
                        className={errors.employmentstatusid ? 'error' : ''}
                        required
                      >
                        <option value="">-- Select Status --</option>
                        {employmentStatuses.map((status) => (
                          <option key={status.statusid} value={status.statusid}>
                            {status.statusname}
                          </option>
                        ))}
                      </select>
                      {errors.employmentstatusid && (
                        <span className="error-message">{errors.employmentstatusid}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="joiningdate">Joining Date</label>
                      <input
                        type="date"
                        id="joiningdate"
                        value={formData.joiningdate}
                        onChange={(e) => setFormData({ ...formData, joiningdate: e.target.value })}
                      />
                    </div>
                  </div>
                <div className="form-row">
                <div className="form-group">
                  <label htmlFor="departmentid">Department</label>
                  <select
                    id="departmentid"
                    value={formData.departmentid}
                    onChange={(e) => setFormData({ ...formData, departmentid: e.target.value })}
                  >
                    <option value="">-- Select Department --</option>
                    {departments.map((dept) => (
                      <option key={dept.departmentid} value={dept.departmentid}>
                        {dept.departmentname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="designationid">Designation</label>
                  <select
                    id="designationid"
                    value={formData.designationid}
                    onChange={(e) => setFormData({ ...formData, designationid: e.target.value })}
                  >
                    <option value="">-- Select Designation --</option>
                    {designations.map((desig) => (
                      <option key={desig.designationid} value={desig.designationid}>
                        {desig.designationname}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="reportingto">Reporting To</label>
                  <select
                    id="reportingto"
                    value={formData.reportingto}
                    onChange={(e) => setFormData({ ...formData, reportingto: e.target.value })}
                  >
                    <option value="">-- Select Manager --</option>
                    {employeesList
                      .filter(emp => emp.employeeid !== formData.employeeid)
                      .map((emp) => (
                      <option key={emp.employeeid} value={emp.employeeid}>
                        {emp.employeename} ({emp.employeecode})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

                            <div className="form-row">
                <div className="form-group">
                  <label htmlFor="leavingdate">Leaving Date</label>
                  <input
                    type="date"
                    id="leavingdate"
                    value={formData.leavingdate}
                    onChange={(e) => setFormData({ ...formData, leavingdate: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="basicsalary">Basic Salary</label>
                  <input
                    type="number"
                    id="basicsalary"
                    value={formData.basicsalary}
                    onChange={(e) => setFormData({ ...formData, basicsalary: e.target.value })}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

              {/* Banking Information */}
              <div className="form-section">
                <h4>Banking Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="accountnumber">Account Number</label>
                    <input
                      type="text"
                      id="accountnumber"
                      value={formData.accountnumber}
                      onChange={(e) => setFormData({ ...formData, accountnumber: e.target.value })}
                      maxLength="50"
                      placeholder="Bank Account Number"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="iban">IBAN</label>
                    <input
                      type="text"
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      maxLength="50"
                      placeholder="International Bank Account Number"
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;